import { useAchievementTypes, useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { AchievementIcon } from 'components/achievements';
import {
  EmptyIndicator,
  FlexContainer,
  LoadingIndicator,
  TitleWithIcon
} from 'components/common';
import { BoxRow } from 'components/common/box';
import { ParticipantsStats } from 'components/common/stats';
import { MyTeamFollowBox, useMyTeamFollowContext } from '.';

import type {
  GetTeamAchievementsCategoriesRes,
  GetTeamAchievementsRes
} from 'shared-types/responses';

const MyTeamAchievements = () => {
  const { messages, user } = useReduxState();

  const achievementTypes = useAchievementTypes();

  const { queryString, queryParams } = useMyTeamFollowContext();

  const { data, loading } = useApiData<GetTeamAchievementsRes>({
    url:
      `/admin/supervisors/${user.id}/team-achievements` +
      (queryString.length ? `?${queryString}` : ''),
    onlyIf: Boolean(user.id)
  });

  const { data: achievementCategories } =
    useApiData<GetTeamAchievementsCategoriesRes>({
      url: `/admin/supervisors/${user.id}/team-achievements-categories`,
      onlyIf: Boolean(user?.id)
    });

  return (
    <MyTeamFollowBox
      numberOfPages={data?.achievements?.length ? data?.pages || 1 : 0}
      title={messages.commonOtherAchievements}
      {...(achievementCategories?.categories?.length
        ? {
            customFilters: [
              {
                allowMultiple: true,
                field: 'categoryId',
                icon: 'tag' as const,
                items: achievementCategories?.categories?.map((category) => ({
                  label: category.title,
                  value: `${category.id}`
                })),
                position: 0,
                title: messages.commonCategories
              }
            ]
          }
        : {})}
    >
      {loading ? (
        <LoadingIndicator />
      ) : data?.achievements?.length ? (
        data.achievements.map((achievement) => (
          <BoxRow key={achievement.id}>
            <div className="row-sm row-center">
              <div className="col">
                <FlexContainer style={{ gap: 15, minWidth: 250 }}>
                  <AchievementIcon size="small" type={achievement.type} />

                  <TitleWithIcon
                    title={achievement.title}
                    titleLink={{
                      as: `/my-team/follow/achievements/${achievement.id}`,
                      href: '/my-team/follow/achievements/[achievementId]'
                    }}
                  >
                    {achievementTypes[achievement.type]?.title}
                  </TitleWithIcon>
                </FlexContainer>
              </div>

              <div className="col-min">
                <ParticipantsStats
                  completionEnabled={true}
                  notStartedLabel={
                    ['conversation', 'group-conversation', 'tasklist'].includes(
                      achievement.type
                    )
                      ? queryParams['participantsOnly'] === 'true'
                        ? messages.tasksUsersNotStartedSingular
                        : messages.commonNotStartedOrNotAdded
                      : messages.commonNotAdded
                  }
                  progressData={achievement.progressData}
                  showInProgress={[
                    'conversation',
                    'group-conversation',
                    'tasklist'
                  ].includes(achievement.type)}
                  showNotStarted={
                    ['conversation', 'group-conversation', 'tasklist'].includes(
                      achievement.type
                    ) || queryParams['participantsOnly'] !== 'true'
                  }
                />
              </div>
            </div>
          </BoxRow>
        ))
      ) : (
        <EmptyIndicator
          icon="check-circle"
          title={messages.achievementsEmpty}
        />
      )}
    </MyTeamFollowBox>
  );
};

export default MyTeamAchievements;
